import { type FormEvent, useState } from 'react'
import { Clock3, File, HardDrive, Ruler } from 'lucide-react'
import { resolveMediaUrl } from '../../../api/client'
import {
  getVisualDerivativeUrl,
  type VisualAsset,
  type VisualGroup
} from '../../../api/visuals'
import { Button, ChipSelect, Modal, ModalActions, StatusBadge } from '../../../components/ui'
import { getVisualAssetMetadataItems, type VisualAssetMetadataItem } from '../formatVisualAssetMetadata'
import styles from './VisualAssetDialog.module.css'

type VisualAssetDialogProps = {
  asset: VisualAsset
  groups: VisualGroup[]
  onClose: () => void
  onSave: (input: {
    id: string
    title: string
    visualGroupIds: string[]
  }) => Promise<void>
}

const metadataIcons: Record<VisualAssetMetadataItem['key'], typeof File> = {
  format: File,
  resolution: Ruler,
  size: HardDrive,
  duration: Clock3
}

const cssClass = (...classNames: Array<string | false | null | undefined>): string =>
  classNames.filter(Boolean).join(' ')

export const VisualAssetDialog = (props: VisualAssetDialogProps) => {
  const [title, setTitle] = useState(props.asset.title)
  const [groupIds, setGroupIds] = useState(props.asset.groups.map((group) => group.id))
  const [saving, setSaving] = useState(false)
  const metadataItems = getVisualAssetMetadataItems(props.asset)
  const toggleGroup = (groupId: string): void => {
    setGroupIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId]
    )
  }

  const save = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setSaving(true)
    await props.onSave({
      id: props.asset.id,
      title,
      visualGroupIds: groupIds
    })
    setSaving(false)
  }

  return (
    <Modal
      ariaLabelledBy="visual-asset-editor-title"
      bodyClassName={styles.body ?? ''}
      closeLabel="Close visual asset editor"
      mobilePresentation="sheet"
      onClose={props.onClose}
      onSubmit={(event) => void save(event)}
      panelClassName={styles.panel ?? ''}
      title={(
        <div className={styles.titleRow}>
          <h2 id="visual-asset-editor-title">{props.asset.title}</h2>
          <StatusBadge label={props.asset.status} />
        </div>
      )}
    >
      <div className={styles.metadataStrip} aria-label="File metadata">
        {metadataItems.map((item) => {
          const Icon = metadataIcons[item.key]

          return (
            <span className={styles.metadataItem} key={item.key} title={item.label}>
              <Icon size={14} />
              <span>{item.value}</span>
            </span>
          )
        })}
      </div>
      <div className={cssClass(styles.previewStage, props.asset.asset_type === 'video' && styles.previewStageVideo)}>
        {props.asset.asset_type === 'video' ? (
          <video controls src={resolveMediaUrl(props.asset.file_url)} />
        ) : (
          <img alt="" src={resolveMediaUrl(getVisualDerivativeUrl(props.asset, 'preview'))} />
        )}
      </div>
      <label className="form-group">
        <span className="form-label">Title</span>
        <input className="form-input" onChange={(event) => setTitle(event.target.value)} value={title} />
      </label>
      <div className="form-group">
        <span className="form-label">Groups</span>
        <ChipSelect
          empty={<span className="muted">No visual groups created.</span>}
          onToggle={toggleGroup}
          options={props.groups.map((group) => ({ id: group.id, label: group.name }))}
          selectedIds={groupIds}
        />
      </div>
      <ModalActions>
        <Button onClick={props.onClose} type="button" variant="secondary">Cancel</Button>
        <Button disabled={saving} type="submit" variant="primary">
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </ModalActions>
    </Modal>
  )
}
